import { supabase } from '@/lib/supabase/client'
import type { ConversationStatus, ConversationPriority } from '@hangarmax/types'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface AdminConversation {
  id: string
  user_id: string
  user_email: string
  subject: string | null
  category: string
  status: string
  priority: string
  context: Record<string, unknown>
  assigned_admin: string | null
  user_unread: number
  admin_unread: number
  user_last_read_at: string | null
  admin_last_read_at: string | null
  last_message_at: string | null
  created_at: string
  updated_at: string
  profiles: { display_name: string | null; is_test?: boolean | null } | null
  /** The same user's OTHER threads that are neither resolved nor archived. */
  user_open_threads?: number
}

export interface AdminMessage {
  id: string
  conversation_id: string
  sender_id: string
  sender_role: string
  body: string | null
  attachments: Array<Record<string, unknown>>
  message_type: string
  metadata: Record<string, unknown>
  created_at: string
  sender_display_name?: string | null
  sender_email?: string | null
}

export interface FeedbackListParams {
  page?: number
  pageSize?: number
  search?: string
  statusFilter?: string[]
  categoryFilter?: string[]
}

export interface FeedbackListResult {
  conversations: AdminConversation[]
  total: number
  page: number
  pageSize: number
}

export interface FeedbackDetailResult {
  conversation: AdminConversation
  messages: AdminMessage[]
}

// ---------------------------------------------------------------------------
// Edge function invocation helper
// ---------------------------------------------------------------------------

async function invokeAdminFeedback<T>(body: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke('admin-feedback', { body })
  if (error) throw new Error(String(error))
  if (data?.error) throw new Error(data.error)
  return data as T
}

// ---------------------------------------------------------------------------
// API
// ---------------------------------------------------------------------------

export function fetchFeedbackList(params: FeedbackListParams) {
  return invokeAdminFeedback<FeedbackListResult>({ action: 'list', ...params })
}

export function fetchFeedbackDetail(conversationId: string) {
  return invokeAdminFeedback<FeedbackDetailResult>({ action: 'detail', conversationId })
}

export function sendAdminReply(conversationId: string, body: string, attachments?: Array<Record<string, unknown>>) {
  return invokeAdminFeedback<{ success: boolean; message: AdminMessage }>({
    action: 'reply',
    conversationId,
    body,
    ...(attachments && attachments.length > 0 ? { attachments } : {}),
  })
}

export function updateConversationStatus(
  conversationId: string,
  status: ConversationStatus,
  notifyUser?: boolean,
) {
  return invokeAdminFeedback<{ success: boolean }>({
    action: 'update_status',
    conversationId,
    status,
    ...(notifyUser ? { notifyUser: true } : {}),
  })
}

export function updateConversationPriority(conversationId: string, priority: ConversationPriority) {
  return invokeAdminFeedback<{ success: boolean }>({
    action: 'update_priority',
    conversationId,
    priority,
  })
}

export function assignConversation(conversationId: string, assignedAdmin: string | null) {
  return invokeAdminFeedback<{ success: boolean }>({
    action: 'assign',
    conversationId,
    assignedAdmin,
  })
}

export function markAdminRead(conversationId: string) {
  return invokeAdminFeedback<{ success: boolean }>({
    action: 'mark_read',
    conversationId,
  })
}

export function fetchFeedbackUnreadCount() {
  return invokeAdminFeedback<{ count: number }>({ action: 'unread_count' })
}
